'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ask, askLive, fetchSuggestions, queryableEntities, type AskOutcome, type Insights, type ModelRef, type QueryResult, type Visualization } from '../lib/api';
import { currentTenant } from '../lib/auth';
import { demoDataset } from '../lib/demo';
import { suggestQuestions, getCachedSuggestions, setCachedSuggestions } from '../lib/suggestions';
import { supportedVisualizations, VIZ_LABELS } from '../lib/widget';
import { Chart, StatTile } from './Chart';
import { InsightPanel } from './InsightPanel';
import { InsightsPanel } from './InsightsPanel';
import { downloadReport } from '../lib/report';
import { listTemplates, type TemplateSummary } from '../lib/templates';

/** Placeholder chips shown while the model is thinking — the API returns at most
 *  six suggestions, and the uneven widths stand in for questions of differing
 *  length so the row does not visibly jump when the real chips replace them. */
const SUGGESTION_SKELETON_WIDTHS = [168, 210, 142, 186, 156, 198];

/**
 * Chat UI (§6c). A question box wired to the NL-query API: on submit it calls
 * `POST /query`, then renders the typed result as a chart (or hero number), a
 * computed insight, a data table, and a disclosure of the exact plan + compiled
 * SQL — so every answer is inspectable. Refusals (out-of-model, invalid plan)
 * render as a clear message rather than a broken chart.
 */
export function Chat({
  model,
  modelRef,
  onAddToTemplate,
}: {
  /** The model itself — used locally by the fallback heuristic, never sent. */
  model: unknown;
  /** What travels to the API: empty for the stored active model, inline offline. */
  modelRef: ModelRef;
  onAddToTemplate?: (report: { title: string; question: string; widget?: string }, templateId?: string) => void;
}): React.JSX.Element {
  const tenant = currentTenant();
  const [question, setQuestion] = useState('');
  const [outcome, setOutcome] = useState<AskOutcome | null>(null);
  const [loading, setLoading] = useState(false);
  // Live (real connector data) by default for a real tenant; demo dataset for `demo`.
  const [live, setLive] = useState(tenant !== null && tenant !== 'demo');
  // Only suggest questions for entities that have a live endpoint (queryable).
  const [queryable, setQueryable] = useState<string[]>([]);
  // Fetch once. `reactStrictMode` double-invokes mount effects in dev, and these
  // are plain fetches (no RTK dedup), so without the guard each fired twice. The
  // ref persists across StrictMode's simulated remount (suppressing the second
  // dev call) but resets on a genuine remount, so a fresh mount still fetches.
  const didLoadEntities = useRef(false);
  useEffect(() => {
    if (didLoadEntities.current) return;
    didLoadEntities.current = true;
    void queryableEntities().then(setQueryable);
  }, []);
  // Suggestions: analyst-quality questions from the API (LLM, grounded in the
  // model); fall back to the local heuristic when the LLM is unavailable.
  //
  // Only the LLM's answer is held in state. Deriving the displayed list instead
  // of storing it keeps `queryable` out of the fetch's dependencies — it arrives
  // a moment later from its own request, and when it was a dependency the new
  // array identity re-ran this effect and asked the API a second time for a
  // result that cannot have changed (`fetchSuggestions` sends only the model).
  // The fallback still sharpens once `queryable` lands, because it is recomputed
  // on render rather than frozen at fetch time.
  //
  // Nothing is suggested until the model answers. The heuristic fallback is for
  // an LLM that is *unavailable*, not for the second it takes to reply — showing
  // its guesses on first paint only to swap them a moment later reads as a
  // glitch, so the chips stay behind a pulse placeholder until the fetch lands.
  const [llmQuestions, setLlmQuestions] = useState<string[] | null>(null);
  const [suggesting, setSuggesting] = useState(true);
  const loadSuggestions = useCallback(
    async (refresh = false) => {
      setSuggesting(true);
      try {
        const q = await fetchSuggestions(modelRef, refresh);
        setLlmQuestions(q);
        setCachedSuggestions(q); // cache so leaving/returning to Ask reuses these
      } finally {
        setSuggesting(false);
      }
    },
    [modelRef],
  );
  // On mount, reuse the session cache if present (reopening Ask unmounts/remounts
  // Chat) — only Refresh re-fetches. Fetch only when nothing is cached. The cache
  // is read in an effect, not a state initializer, to stay SSR-safe (no window on
  // the server render → no hydration mismatch). The ref suppresses StrictMode's
  // dev double-invoke before the cache is populated.
  const lastSuggestLoad = useRef<typeof loadSuggestions | null>(null);
  useEffect(() => {
    const cached = getCachedSuggestions();
    if (cached !== null) {
      setLlmQuestions(cached);
      setSuggesting(false);
      return;
    }
    if (lastSuggestLoad.current === loadSuggestions) return;
    lastSuggestLoad.current = loadSuggestions;
    void loadSuggestions(false);
  }, [loadSuggestions]);
  const questions = llmQuestions?.length ? llmQuestions : suggestQuestions(model, queryable);

  async function run(q: string): Promise<void> {
    const query = q.trim();
    if (!query) return;
    setQuestion(query);
    setLoading(true);
    setOutcome(live ? await askLive(query, modelRef) : await ask({ question: query, ...modelRef, dataset: demoDataset }));
    setLoading(false);
  }

  return (
    <div className="card">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run(question);
        }}
        style={{ display: 'flex', gap: 8 }}
      >
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask a question about your data…"
          aria-label="Ask a question"
          style={{
            flex: 1,
            padding: '10px 12px',
            fontSize: 15,
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--plane)',
            color: 'var(--text-primary)',
          }}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '10px 18px',
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 8,
            border: 'none',
            background: 'var(--series-1)',
            color: '#fff',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Asking…' : 'Ask'}
        </button>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 13 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
          Live data
        </label>
        {live ? (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 999,
              fontSize: 12,
              background: 'color-mix(in srgb, var(--series-2) 18%, transparent)',
              color: 'var(--series-2)',
            }}
          >
            ● live · {tenant ?? 'tenant'} · raw rows never leave the network
          </span>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>
            demo dataset
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <span className="muted" style={{ fontSize: 12 }}>
          Try these — suggested from the model
        </span>
        <button
          type="button"
          onClick={() => void loadSuggestions(true)}
          disabled={suggesting}
          title="Suggest different questions from the model"
          style={{
            padding: '3px 10px',
            fontSize: 12,
            borderRadius: 999,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: suggesting ? 'default' : 'pointer',
            opacity: suggesting ? 0.6 : 1,
          }}
        >
          ↻ Refresh
        </button>
      </div>

      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}
        role="group"
        aria-label="Suggested questions"
        aria-busy={suggesting}
      >
        {suggesting
          ? SUGGESTION_SKELETON_WIDTHS.map((width) => (
              <span key={width} className="chip-skeleton" style={{ width }} aria-hidden="true" />
            ))
          : questions.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => void run(q)}
                style={{
                  padding: '6px 12px',
                  fontSize: 13,
                  borderRadius: 999,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                {q}
              </button>
            ))}
      </div>

      {outcome ? (
        <Outcome
          outcome={outcome}
          question={question}
          onRefine={(option) => void run(`${question} — ${option}`)}
          onAddToTemplate={onAddToTemplate}
        />
      ) : null}
    </div>
  );
}

function Outcome({
  outcome,
  question,
  onRefine,
  onAddToTemplate,
}: {
  outcome: AskOutcome;
  question: string;
  onRefine: (option: string) => void;
  onAddToTemplate?: (report: { title: string; question: string; widget?: string }, templateId?: string) => void;
}): React.JSX.Element {
  const [addOpen, setAddOpen] = useState(false);
  // The visualization the user picked (undefined until they touch the switcher).
  const [pickedViz, setPickedViz] = useState<Visualization | undefined>(undefined);
  // A new answer resets the picker back to the planner's default.
  useEffect(() => { setPickedViz(undefined); }, [outcome]);
  // Too ambiguous to default safely → ask the user, don't guess or dead-end.
  if (!outcome.ok && outcome.stage === 'clarification') {
    return (
      <div style={{ marginTop: 20, padding: 14, borderRadius: 8, background: 'var(--plane)' }}>
        <strong>{outcome.clarification.question}</strong>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          {outcome.clarification.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onRefine(opt)}
              style={{
                padding: '7px 12px',
                fontSize: 14,
                borderRadius: 999,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (!outcome.ok) {
    return (
      <div style={{ marginTop: 20, padding: 14, borderRadius: 8, background: 'var(--plane)' }}>
        <strong>Couldn’t answer that.</strong>
        <div style={{ color: 'var(--text-secondary)', marginTop: 4, fontSize: 14 }}>{outcome.reason}</div>
        {outcome.errors?.length ? (
          <ul style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {outcome.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
  const assumptions = outcome.assumptions ?? [];
  const note =
    assumptions.length > 0 ? (
      <div
        style={{
          marginTop: 16,
          padding: '10px 14px',
          borderRadius: 8,
          background: 'var(--plane)',
          fontSize: 13,
          color: 'var(--text-secondary)',
        }}
      >
        <strong style={{ color: 'var(--text)' }}>Assumptions</strong> — filled in for an underspecified
        question; refine your wording to change any:
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </div>
    ) : null;

  if (!outcome.result) {
    return (
      <div style={{ marginTop: 20, color: 'var(--text-secondary)', fontSize: 14 }}>
        {note}
        Planned the query but no data was supplied. Compiled SQL:
        <pre style={sqlStyle}>{outcome.sql}</pre>
      </div>
    );
  }
  const supported = supportedVisualizations(outcome.result);
  const planned = outcome.result.plan.visualization;
  const viz: Visualization =
    pickedViz && supported.includes(pickedViz)
      ? pickedViz
      : planned && supported.includes(planned)
        ? planned
        : supported[0]!;

  return (
    <>
      {note}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        {onAddToTemplate ? (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            style={exportBtnStyle}
            title="Add this report to a template (new or existing)"
          >
            + Add to report
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => downloadReport(question, outcome.result!, outcome.insights ?? null)}
          style={exportBtnStyle}
          title="Download a self-contained report (open or print to PDF)"
        >
          ⭳ Export report
        </button>
      </div>
      {onAddToTemplate && addOpen ? (
        <AddToReportDialog
          onClose={() => setAddOpen(false)}
          onConfirm={(templateId) => {
            onAddToTemplate({ title: question, question, widget: viz }, templateId);
            setAddOpen(false);
          }}
        />
      ) : null}
      <ResultView result={outcome.result} insights={outcome.insights} viz={viz} supported={supported} onViz={setPickedViz} />
    </>
  );
}

/**
 * "Add to report" dialog: choose whether this report becomes a brand-new template,
 * or is added into an existing one via a searchable list of the tenant's templates.
 * Confirming hands the chosen `templateId` (or none) back so the templates tab can
 * open the builder in edit mode with the report already loaded.
 */
function AddToReportDialog({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (templateId?: string) => void;
}): React.JSX.Element {
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    void listTemplates().then(setTemplates).finally(() => setLoading(false));
  }, []);

  const filtered = templates.filter((t) => t.name.toLowerCase().includes(search.trim().toLowerCase()));
  const canConfirm = mode === 'new' || Boolean(selectedId);

  return (
    <div style={overlayStyle} onClick={onClose} role="presentation">
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Add to report">
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Add to report</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <label style={radioRow}>
            <input type="radio" name="add-mode" checked={mode === 'new'} onChange={() => setMode('new')} />
            As a new report
          </label>
          <label style={radioRow}>
            <input type="radio" name="add-mode" checked={mode === 'existing'} onChange={() => setMode('existing')} />
            Add to an existing report
          </label>

          {mode === 'existing' ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search a report…"
                style={dialogInputStyle}
                autoFocus
              />
              <div style={listStyle}>
                {loading ? (
                  <div style={emptyRow}>Loading…</div>
                ) : filtered.length === 0 ? (
                  <div style={emptyRow}>No matching reports.</div>
                ) : (
                  filtered.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedId(t.id)}
                      style={{ ...optionStyle, ...(selectedId === t.id ? optionSelected : {}) }}
                    >
                      <span>{t.name}</span>
                      <span className="muted" style={{ fontSize: 12 }}>{t.reportCount} report{t.reportCount === 1 ? '' : 's'}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} style={dialogGhostBtn}>Cancel</button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => onConfirm(mode === 'existing' ? selectedId ?? undefined : undefined)}
            style={{ ...dialogPrimaryBtn, opacity: canConfirm ? 1 : 0.5, cursor: canConfirm ? 'pointer' : 'default' }}
          >
            {mode === 'existing' ? 'Open & edit' : 'Create report'}
          </button>
        </div>
      </div>
    </div>
  );
}

const exportBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid var(--border, rgba(120,120,140,0.3))',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

function ResultView({ result, insights, viz, supported, onViz }: {
  result: QueryResult;
  insights?: Insights | null;
  viz?: Visualization;
  supported?: Visualization[];
  onViz?: (v: Visualization) => void;
}): React.JSX.Element {
  const metricCol = result.columns.find((c) => c.kind === 'metric');
  const effectiveViz = viz ?? result.plan.visualization;
  const vizResult: QueryResult =
    effectiveViz && effectiveViz !== result.plan.visualization
      ? { ...result, plan: { ...result.plan, visualization: effectiveViz } }
      : result;
  const showKpi = effectiveViz === 'number' && !!metricCol;

  return (
    <div style={{ marginTop: 20 }}>
      {supported && onViz && supported.length > 1 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 4 }}>Show as</span>
          {supported.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onViz(v)}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                borderRadius: 999,
                cursor: 'pointer',
                border: '1px solid var(--border)',
                background: v === viz ? 'var(--series-1, #2f6fed)' : 'var(--surface)',
                color: v === viz ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {VIZ_LABELS[v]}
            </button>
          ))}
        </div>
      ) : null}
      {showKpi ? (
        <StatTile label={metricCol!.label} value={result.rows[0]?.[metricCol!.key] ?? '—'} />
      ) : (
        <Chart result={vizResult} />
      )}
      {/* Rich server insights (AI or deterministic) when present; else the local one-liner. */}
      {insights ? <InsightsPanel insights={insights} /> : <InsightPanel result={result} />}

      <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {result.columns.map((c) => (
              <th
                key={c.key}
                style={{
                  textAlign: c.kind === 'metric' ? 'right' : 'left',
                  padding: '6px 8px',
                  borderBottom: '1px solid var(--border)',
                  color: 'var(--text-muted)',
                  fontWeight: 600,
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row, i) => (
            <tr key={i}>
              {result.columns.map((c) => (
                <td
                  key={c.key}
                  style={{
                    textAlign: c.kind === 'metric' ? 'right' : 'left',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--grid)',
                    fontVariantNumeric: c.kind === 'metric' ? 'tabular-nums' : undefined,
                  }}
                >
                  {String(row[c.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <details style={{ marginTop: 14 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13 }}>
          Plan &amp; compiled SQL
        </summary>
        <pre style={sqlStyle}>{result.sql}</pre>
      </details>
    </div>
  );
}

const sqlStyle: React.CSSProperties = {
  marginTop: 8,
  padding: 12,
  borderRadius: 8,
  background: 'var(--plane)',
  border: '1px solid var(--border)',
  fontSize: 12.5,
  overflowX: 'auto',
  color: 'var(--text-secondary)',
};

// ── "Add to report" dialog ──
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const dialogStyle: React.CSSProperties = {
  width: 'min(460px, 100%)', background: 'var(--surface, #fff)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', borderRadius: 12, padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
};
const radioRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' };
const dialogInputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--plane, transparent)', color: 'var(--text-primary)', fontSize: 14,
};
const listStyle: React.CSSProperties = {
  maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, display: 'grid', gap: 2, padding: 4,
};
const emptyRow: React.CSSProperties = { padding: 10, color: 'var(--text-muted)', fontSize: 13 };
const optionStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px',
  borderRadius: 6, border: '1px solid transparent', background: 'transparent', color: 'var(--text-primary)',
  cursor: 'pointer', textAlign: 'left', width: '100%',
};
const optionSelected: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--series-1) 14%, transparent)', border: '1px solid var(--series-1)',
};
const dialogGhostBtn: React.CSSProperties = {
  padding: '7px 14px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)',
  background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
};
const dialogPrimaryBtn: React.CSSProperties = {
  padding: '7px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8,
  border: '1px solid var(--series-1)', background: 'var(--series-1)', color: '#fff',
};
